import { createBrowserRouter, Outlet, RouterProvider, type RouteObject } from 'react-router-dom';
import RouteErrorFallback from '@/components/common/RouteErrorFallback';

import AppShell from '@/components/layout/AppShell.tsx';
import RequireAuth from './RequireAuth';
import PublicOnly from './PublicOnly';
import RequireNS from '@/routes/RequireNS.tsx';
import StaffLinkSignedOut from './StaffLinkSignedOut';
import AblyAuth from './AblyAuth';
import PublicLiveLayout from '@/routes/PublicLiveLayout';
import WaitingRoomPage from '@/pages/public/WaitingRoomPage';
import RoleToken from './RoleToken';

import LoginPage from '@/pages/auth/LoginPage.tsx';
import RegisterPage from '@/pages/auth/RegisterPage.tsx';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage.tsx';
import NewPasswordPage from '@/pages/auth/NewPasswordPage.tsx';
import AuthLinkErrorPage from '@/pages/auth/AuthLinkErrorPage.tsx';
import SelectWorkspacePage from '@/pages/auth/SelectWorkspacePage.tsx';

import NewUserInformationPage from '@/pages/new-user/NewUserInformationPage';
import TeamPage from '@/pages/team/TeamPage';

import Dashboard from '@/pages/Dashboard.tsx';
import WebinarsPage from '@/pages/webinars/WebinarsPage';
import WebinarSessionPage from '@/components/pages/webinars/webinar-session/WebinarSessionPage';
import AdminLivePage from '@/pages/public/AdminLivePage';
import EvergreenTestPage from '@/pages/public/EvergreenTestPage';

import ConfigurationSection from '@/pages/create-webinar/ConfigurationSection';
import SigninSection from '@/pages/create-webinar/SigninSection';
import WaitingRoomSection from '@/pages/create-webinar/WaitingRoomSection';
import LiveSection from '@/pages/create-webinar/LiveSection';
import ReplaySection from '@/pages/create-webinar/ReplaySection';
import ThankYouSection from '@/pages/create-webinar/ThankYouSection';
import IntegrationSection from '@/pages/create-webinar/IntegrationSection';
import EvergreenSection from '@/pages/create-webinar/EvergreenSection';
import ChatEvergreenSection from '@/pages/create-webinar/ChatEvergreenSection';
import CreateWebinarDataLoader from '@/pages/create-webinar/CreateWebinarDataLoader';

import SigninAttendeePage from '@/pages/public/SigninAttendeePage';
import WebinarErrorPage from '@/pages/public/WebinarErrorPage';
import ThankYouPage from '@/pages/ThankYouPage';
import { WebinarLanguageProvider } from '@/contexts/WebinarLanguageContext';

import IntegrationsPage from '@/pages/integration/IntegrationsPage';
import GhlOAuthCallbackPage from '@/pages/integration/GhlOAuthCallbackPage';
import ConfigurationPage from '@/pages/configuration/ConfigurationPage';
import BillingPage from '@/pages/billing/BillingPage';
import BillingRegularizationPage from '@/pages/billing/BillingRegularizationPage';
import { BILLING_REGULARIZATION_PATH } from '@/lib/billingRegularization';
import Analytics from '@/pages/Analytics.tsx';
import { SessionDetail } from '@/features/analytics/pages/SessionDetail';

import LiveKitRecordingViewPage from '@/pages/LkRecordingViewPage';
import InviteToWorkspacePage from '@/pages/auth/InviteToWorkspacePage';

import NotFoundPublic from '@/pages/NotFoundPublic';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import InteractionSection from '@/pages/create-webinar/InteractionSection';
import { StripeProvider } from '@/provider/StripeProvider';

function ProtectedLayout() {
  return (
    <RequireAuth>
      <StripeProvider>
        <AppShell>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </AppShell>
      </StripeProvider>
    </RequireAuth>
  );
}

function ProtectedLayoutCreateWebinar({
  createWebinar = true,
}: Readonly<{ createWebinar?: boolean }>) {
  return (
    <RequireAuth>
      <StripeProvider>
        <AppShell createWebinar={createWebinar}>
          <ErrorBoundary>
            <CreateWebinarDataLoader />
            <Outlet />
          </ErrorBoundary>
        </AppShell>
      </StripeProvider>
    </RequireAuth>
  );
}

function withErrorBoundary(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => {
    const childrenOverride = route.children ? { children: withErrorBoundary(route.children) } : {};
    return {
      ...route,
      errorElement: route.errorElement ?? <RouteErrorFallback />,
      ...childrenOverride,
    } as RouteObject;
  });
}

const router = createBrowserRouter(
  withErrorBoundary([
    {
      path: '/login',
      element: (
        <PublicOnly>
          <RequireNS ns={['auth', 'common']}>
            <ErrorBoundary>
              <LoginPage />
            </ErrorBoundary>
          </RequireNS>
        </PublicOnly>
      ),
    },
    {
      path: '/register',
      element: (
        <PublicOnly>
          <RequireNS ns={['register', 'auth', 'common']}>
            <ErrorBoundary>
              <RegisterPage />
            </ErrorBoundary>
          </RequireNS>
        </PublicOnly>
      ),
    },
    {
      path: '/forgot-password',
      element: (
        <PublicOnly>
          <RequireNS ns={['auth', 'common']}>
            <ErrorBoundary>
              <ForgotPasswordPage />
            </ErrorBoundary>
          </RequireNS>
        </PublicOnly>
      ),
    },
    {
      path: '/new-password',
      element: (
        <PublicOnly>
          <RequireNS ns={['auth', 'common']}>
            <ErrorBoundary>
              <NewPasswordPage />
            </ErrorBoundary>
          </RequireNS>
        </PublicOnly>
      ),
    },
    // Cibles de redirection du proxy d'authentification back (lien de mail invalide, expiré ou en erreur).
    {
      path: '/auth/link-expired',
      element: (
        <RequireNS ns={['auth', 'common']}>
          <ErrorBoundary>
            <AuthLinkErrorPage variant="expired" />
          </ErrorBoundary>
        </RequireNS>
      ),
    },
    {
      path: '/auth/error',
      element: (
        <RequireNS ns={['auth', 'common']}>
          <ErrorBoundary>
            <AuthLinkErrorPage variant="error" />
          </ErrorBoundary>
        </RequireNS>
      ),
    },
    {
      path: '/user-information',
      element: (
        <RequireAuth>
          <RequireNS ns={['auth', 'common']}>
            <ErrorBoundary>
              <NewUserInformationPage />
            </ErrorBoundary>
          </RequireNS>
        </RequireAuth>
      ),
    },
    {
      path: '/invite',
      element: (
        <RequireAuth>
          <RequireNS ns={['auth', 'common']}>
            <ErrorBoundary>
              <InviteToWorkspacePage />
            </ErrorBoundary>
          </RequireNS>
        </RequireAuth>
      ),
    },
    {
      path: '/select-workspaces',
      element: (
        <RequireAuth>
          <RequireNS ns={['auth', 'common']}>
            <ErrorBoundary>
              <SelectWorkspacePage />
            </ErrorBoundary>
          </RequireNS>
        </RequireAuth>
      ),
    },
    {
      path: '/signin-attendee/:webinarId/:sessionIdentifier',
      element: (
        <WebinarLanguageProvider>
          <RequireNS ns={['auth', 'common', 'webinars']}>
            <ErrorBoundary>
              <SigninAttendeePage />
            </ErrorBoundary>
          </RequireNS>
        </WebinarLanguageProvider>
      ),
    },
    {
      path: '/signin-replay/:webinarId/:sessionIdentifier',
      element: (
        <WebinarLanguageProvider>
          <RequireNS ns={['auth', 'common', 'webinars']}>
            <ErrorBoundary>
              <SigninAttendeePage mode="replay" />
            </ErrorBoundary>
          </RequireNS>
        </WebinarLanguageProvider>
      ),
    },
    {
      path: '/webinar-error',
      element: (
        <RequireNS ns={['common']}>
          <ErrorBoundary>
            <WebinarErrorPage />
          </ErrorBoundary>
        </RequireNS>
      ),
    },
    // URL de redirection OAuth de l'app GoHighLevel : publique, sans « highlevel » (refusé par GHL).
    {
      path: '/integrations/ghl/callback',
      element: (
        <RequireNS ns={['integrations', 'common']}>
          <ErrorBoundary>
            <GhlOAuthCallbackPage />
          </ErrorBoundary>
        </RequireNS>
      ),
    },
    {
      path: '/admin-live/:webinarId/:sessionId',
      element: (
        <RequireAuth>
          <RequireNS ns={['auth', 'common', 'webinars']}>
            <AblyAuth admin>
              <ErrorBoundary>
                <StripeProvider>
                  <AdminLivePage />
                </StripeProvider>
              </ErrorBoundary>
            </AblyAuth>
          </RequireNS>
        </RequireAuth>
      ),
    },
    {
      path: '/admin-live/:webinarId/:sessionId/:staffLinkId',
      // Les traductions sont chargées avant la garde : le refus doit pouvoir se dire sans session.
      element: (
        <RequireNS ns={['auth', 'common', 'webinars']}>
          <RequireAuth unauthenticatedFallback={<StaffLinkSignedOut />}>
            <AblyAuth admin>
              <ErrorBoundary>
                <StripeProvider>
                  <AdminLivePage />
                </StripeProvider>
              </ErrorBoundary>
            </AblyAuth>
          </RequireAuth>
        </RequireNS>
      ),
    },
    {
      // Salle de test Evergreen : ni Ably, ni Stripe, ni coque du back-office.
      path: '/evergreen-test/:workspaceId/:webinarId/:testRunId?',
      element: (
        <RequireAuth>
          <RequireNS ns={['auth', 'common', 'webinars', 'createWebinar']}>
            <ErrorBoundary>
              <EvergreenTestPage />
            </ErrorBoundary>
          </RequireNS>
        </RequireAuth>
      ),
    },
    {
      path: '/recording-view/:breakpoint?',
      element: (
        <RequireNS ns={['common', 'webinars']}>
          <LiveKitRecordingViewPage />
        </RequireNS>
      ),
    },
    {
      element: <PublicLiveLayout />,
      children: [
        {
          path: '/waiting-room/:webinarId/:sessionIdentifier',
          element: <WaitingRoomPage />,
        },
        {
          path: '/live/:webinarId/:sessionIdentifier',
          element: <RoleToken />,
        },
      ],
    },
    {
      path: '/thank-you/:webinarId/:sessionIdentifier',
      element: (
        <WebinarLanguageProvider>
          <RequireNS ns={['auth', 'common', 'webinars']}>
            <ErrorBoundary>
              <ThankYouPage />
            </ErrorBoundary>
          </RequireNS>
        </WebinarLanguageProvider>
      ),
    },
    {
      element: <ProtectedLayout />,
      children: [
        { path: '/', element: <Dashboard /> },
        { path: '/webinars', element: <WebinarsPage /> },
        { path: '/webinars/:webinarId', element: <WebinarSessionPage /> },
        { path: 'analytics', element: <Analytics /> },
        { path: 'analytics/sessions/:sessionId', element: <SessionDetail /> },
        { path: '/integrations', element: <IntegrationsPage /> },
        {
          path: '/team',
          element: (
            <RequireNS ns={['team', 'common']}>
              <TeamPage />
            </RequireNS>
          ),
        },
        { path: '/settings', element: <ConfigurationPage /> },
        { path: '/billing', element: <BillingPage /> },
        { path: BILLING_REGULARIZATION_PATH, element: <BillingRegularizationPage /> },
      ],
    },
    {
      element: <ProtectedLayoutCreateWebinar />,
      children: [
        { path: '/create-webinar/configuration/:webinarId?', element: <ConfigurationSection /> },
        { path: '/create-webinar/signin/:webinarId?', element: <SigninSection /> },
        { path: '/create-webinar/waiting-room/:webinarId?', element: <WaitingRoomSection /> },
        { path: '/create-webinar/live/:webinarId?', element: <LiveSection /> },
        { path: '/create-webinar/evergreen/:webinarId?', element: <EvergreenSection /> },
        {
          path: '/create-webinar/chat-evergreen/:webinarId?',
          element: <ChatEvergreenSection />,
        },
        { path: '/create-webinar/interactions/:webinarId?', element: <InteractionSection /> },
        { path: '/create-webinar/post-event/:webinarId?', element: <ThankYouSection /> },
        { path: '/create-webinar/replay/:webinarId?', element: <ReplaySection /> },
        { path: '/create-webinar/integration/:webinarId?', element: <IntegrationSection /> },
      ],
    },
    {
      path: '*',
      element: <NotFoundPublic />,
    },
  ]),
);

export default function AppRouter() {
  return <RouterProvider router={router} />;
}
